import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { t, applyTranslations, setLanguage, currentLang } from './js/lang.js';

// --- 1. DYNAMIC ORIENTATION HUD ---
const orientationUI = document.createElement('div');
orientationUI.style.cssText = `
    position: absolute; top: 120px; left: 50%; transform: translateX(-50%);
    background: rgba(15,17,21,0.85); border: 1px solid #333a45; border-radius: 8px;
    padding: 10px 20px; color: #a0aec0; font-size: 0.85rem; font-weight: bold;
    display: none; z-index: 100; backdrop-filter: blur(5px);
    box-shadow: 0 4px 15px rgba(0,0,0,0.5); letter-spacing: 1px;
`;
document.body.appendChild(orientationUI);

function updateOrientationUI() {
    orientationUI.innerHTML = `<span style="color:#60a5fa">⬅ ${t('zoneLoading')}</span> &nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp; <span style="color:#f97316">${t('zoneProd')} ➡</span>`;
}

// --- 2. LANGUAGE SETUP ---
window.changeLang = (lang) => {
    setLanguage(lang);
    const btnEn = document.getElementById('btn-lang-en');
    const btnCz = document.getElementById('btn-lang-cz');
    if (btnEn) btnEn.className = lang === 'en' ? 'lang-btn lang-active' : 'lang-btn lang-inactive';
    if (btnCz) btnCz.className = lang === 'cz' ? 'lang-btn lang-active' : 'lang-btn lang-inactive';
    
    const statusEl = document.getElementById('status');
    if (statusEl && statusEl.innerText !== t('statusCalc')) statusEl.innerText = t('statusOnline');
    updateOrientationUI();
};
applyTranslations();
window.changeLang(currentLang);

// --- 3. SCENE & ENVIRONMENT SETUP ---
const rows = 8, segments = 8, levels = 5;
const scene = new THREE.Scene();
scene.background = new THREE.Color(0x0b0c10); 

const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
camera.position.set(15, 20, 20);

const renderer = new THREE.WebGLRenderer({ canvas: document.querySelector('#bg'), antialias: true });
renderer.setSize(window.innerWidth, window.innerHeight);

const controls = new OrbitControls(camera, renderer.domElement);
controls.target.set(10, 2, 5);

// 22.3.: Instantly give camera control back to the user when they click/drag
controls.addEventListener('start', () => {
    isCameraMoving = false; 
});

const light = new THREE.DirectionalLight(0xffffff, 1);
light.position.set(5, 10, 7);
scene.add(light);
scene.add(new THREE.AmbientLight(0x404040));

// ENVIRONMENT MESHES (Floor & Zones)
const floorGroup = new THREE.Group();

// Dark Floor
const floor = new THREE.Mesh(
    new THREE.PlaneGeometry(60, 40), 
    new THREE.MeshStandardMaterial({ color: 0x111218, roughness: 0.9 })
);
floor.rotation.x = -Math.PI / 2;
floor.position.set(10, -0.5, 4);
floorGroup.add(floor);

// Loading Dock (Blue - Left Side / Z = -3)
const dock = new THREE.Mesh(
    new THREE.BoxGeometry(40, 0.2, 3), 
    new THREE.MeshStandardMaterial({ color: 0x1e3a8a })
);
dock.position.set(10, -0.4, -3);
floorGroup.add(dock);

// Production Line (Orange - Right Side / Z = 11)
const prod = new THREE.Mesh(
    new THREE.BoxGeometry(40, 0.2, 3), 
    new THREE.MeshStandardMaterial({ color: 0x9a3412 })
);
prod.position.set(10, -0.4, 11);
floorGroup.add(prod);

scene.add(floorGroup);


// --- 4. STATE & CAMERA ANIMATION ---
let isHeatmapMode = false, hideLowRunners = false;
let currentAisleFilter = 'ALL', currentPNFilter = 'ALL';
let globalWarehouseData = [];

// Smooth Camera Targets
let targetCameraPos = new THREE.Vector3(15, 20, 20);
let targetCameraLookAt = new THREE.Vector3(10, 2, 5);
let isCameraMoving = false;

async function loadWarehouse() {
    try {
        let endpoint = '/api/warehouse';
        if (isHeatmapMode) {
            const timeFilter = document.getElementById('filter-time');
            endpoint = `/api/heatmap?days=${timeFilter ? timeFilter.value : 'all'}`; 
        }

        const response = await fetch(endpoint); 
        globalWarehouseData = await response.json();
        populatePNDropdown(globalWarehouseData);
        generateBoxes(globalWarehouseData);

        const statusEl = document.getElementById('status');
        const statusDot = document.getElementById('status-dot');
        if (statusEl) { statusEl.innerText = t('statusOnline'); statusEl.style.color = '#4ade80'; }
        if (statusDot) statusDot.style.background = '#4ade80';

    } catch (error) {
        console.error("CRITICAL ERROR:", error);
        const statusEl = document.getElementById('status');
        if (statusEl) { statusEl.innerText = t('statusOffline'); statusEl.style.color = '#ef4444'; }
    }
}

function populatePNDropdown(data) {
    const dataList = document.getElementById('pn-options');
    if (!dataList) return; 
    const uniquePNs = new Set();
    data.forEach(slot => { if (slot.part_numbers) slot.part_numbers.forEach(pn => uniquePNs.add(pn)); });
    let html = '';
    Array.from(uniquePNs).sort().forEach(pn => html += `<option value="${pn}">`);
    dataList.innerHTML = html;
}

// --- 5. RENDER LOGIC ---
function drawScaffolding() {
    const rackMaterial = new THREE.MeshBasicMaterial({ color: 0xcccccc, wireframe: true, opacity: 0.1, transparent: true });
    for (let a = 1; a <= rows; a++) {
        if (currentAisleFilter !== 'ALL' && a !== parseInt(currentAisleFilter)) continue;
        for (let s = 1; s <= segments; s++) {
            for (let l = 1; l <= levels; l++) {
                const x = (Math.floor((a - 1) / 2) * 5) + (((a - 1) % 2) * 3);
                const frame = new THREE.Mesh(new THREE.BoxGeometry(0.85, 0.85, 0.85), rackMaterial);
                frame.position.set(x, (l - 1) * 1.1, (s - 1) * 1.2);
                scene.add(frame);
            }
        }
    }
}

function generateBoxes(warehouseData) {
    // 1. Clear existing meshes (boxes and scaffolding)
    for (let i = scene.children.length - 1; i >= 0; i--) {
        // FIX: Because the Floor/Docks are inside a Group, 
        // we can safely delete all loose 'Mesh' objects without destroying the floor.
        if (scene.children[i].type === 'Mesh') {
            scene.remove(scene.children[i]);
        }
    }
    
    drawScaffolding();

    // 
    const geometry = new THREE.BoxGeometry(0.8, 0.8, 0.8);
    let p99 = 0, p90 = 0, p75 = 0, p50 = 0;
    
    if (isHeatmapMode) {
        const activeCounts = warehouseData.map(s => parseInt(s.activity_count || 0)).filter(c => c > 0).sort((a, b) => a - b);
        if (activeCounts.length > 0) {
            p99 = activeCounts[Math.floor(activeCounts.length * 0.99)] || activeCounts[activeCounts.length - 1];
            p90 = activeCounts[Math.floor(activeCounts.length * 0.90)] || 0;
            p75 = activeCounts[Math.floor(activeCounts.length * 0.75)] || 0;
            p50 = activeCounts[Math.floor(activeCounts.length * 0.50)] || 0;
            
            const lPanel = document.getElementById('heatmap-legend-panel');
            if (lPanel && lPanel.style.display !== 'none') {
                document.getElementById('val-p99').innerText = `≥ ${p99}`;
                document.getElementById('val-p90').innerText = `≥ ${p90}`;
                document.getElementById('val-p75').innerText = `≥ ${p75}`;
                document.getElementById('val-p50').innerText = `≥ ${p50}`;
            }
        }
    }

    warehouseData.forEach(slot => {
        if (currentAisleFilter !== 'ALL' && slot.aisle !== parseInt(currentAisleFilter)) return;
        if (!isHeatmapMode && currentPNFilter !== 'ALL') {
            if (!slot.part_numbers || !slot.part_numbers.includes(currentPNFilter)) return;
        }

        let color = 0xcccccc, opacity = 1, transparent = false, shouldDraw = true;

        if (isHeatmapMode) {
            const count = parseInt(slot.activity_count || 0);
            if (count === 0) shouldDraw = false; 
            else if (hideLowRunners && count < p50) shouldDraw = false; 
            else if (count >= p99 && count > p90) color = 0xff00ff; 
            else if (count >= p90) color = 0xe74c3c; 
            else if (count >= p75) color = 0xf39c12; 
            else if (count >= p50) color = 0xf1c40f; 
            else color = 0x2ecc71;                   
        } else {
            if (slot.status === 'EMPTY' || !slot.status) shouldDraw = false;
            else {
                switch (slot.status) {
                    case 'OCCUPIED': color = 0x2ecc71; break;
                    case 'INBOUND':  color = 0x3498db; break;
                    case 'RESERVED': color = 0xf39c12; break;
                    case 'BLOCKED':  color = 0xe74c3c; break;
                }
            }
        }

        if (!shouldDraw) return;

        const box = new THREE.Mesh(geometry, new THREE.MeshStandardMaterial({ color: color, roughness: 0.7, metalness: 0.2 }));
        const aisleIndex = slot.aisle - 1;
        box.position.x = (Math.floor(aisleIndex / 2) * 5) + ((aisleIndex % 2) * 3);
        box.position.z = (slot.section - 1) * 1.2;
        box.position.y = (slot.level - 1) * 1.1;

        box.userData = { id: slot.id, coord: `R${slot.aisle}-S${slot.section}-L${slot.level}` };
        scene.add(box);
    });
}

// --- 6. EVENT LISTENERS & CAMERA MATH ---
window.addEventListener('resize', () => {
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(window.innerWidth, window.innerHeight);
});

// The Magic Camera Move!
document.getElementById('filter-aisle')?.addEventListener('change', (e) => {
    currentAisleFilter = e.target.value; 
    generateBoxes(globalWarehouseData);
    
    if (currentAisleFilter !== 'ALL') {
        const a = parseInt(currentAisleFilter);
        const aisleX = (Math.floor((a - 1) / 2) * 5) + (((a - 1) % 2) * 3);
        
        // Move camera left of the aisle (-12), look straight at the aisle. 
        // This ensures the Blue Dock (Z=-3) is on the Left, and Orange Prod (Z=11) is on the Right!
        targetCameraPos.set(aisleX - 12, 4, 4.2);
        targetCameraLookAt.set(aisleX, 2, 4.2);
        isCameraMoving = true;
        
        orientationUI.style.display = 'block';
    } else {
        targetCameraPos.set(15, 20, 20);
        targetCameraLookAt.set(10, 2, 5);
        isCameraMoving = true;
        orientationUI.style.display = 'none';
    }
});

document.getElementById('reset-view')?.addEventListener('click', () => {
    const aisleFilter = document.getElementById('filter-aisle');
    if (aisleFilter) aisleFilter.value = 'ALL';
    currentAisleFilter = 'ALL';
    generateBoxes(globalWarehouseData);
    
    targetCameraPos.set(15, 20, 20);
    targetCameraLookAt.set(10, 2, 5);
    isCameraMoving = true;
    orientationUI.style.display = 'none';
});

document.getElementById('filter-pn')?.addEventListener('input', (e) => {
    currentPNFilter = e.target.value.trim() === '' ? 'ALL' : e.target.value.trim();
    generateBoxes(globalWarehouseData);
});

document.getElementById('filter-time')?.addEventListener('change', () => {
    if (isHeatmapMode) {
        document.getElementById('status').innerText = t('statusCalc');
        document.getElementById('status').style.color = '#f59e0b';
        loadWarehouse();
    }
});

document.getElementById('config-hide-low')?.addEventListener('change', (e) => {
    hideLowRunners = e.target.checked;
    generateBoxes(globalWarehouseData);
});

document.getElementById('toggle-heatmap')?.addEventListener('click', () => {
    try {
        isHeatmapMode = !isHeatmapMode; 
        const btn = document.getElementById('toggle-heatmap');
        const stdControls = document.getElementById('standard-controls');
        const hmControls = document.getElementById('heatmap-controls');
        const legendPanel = document.getElementById('heatmap-legend-panel');
        
        if (isHeatmapMode) {
            if(btn) { btn.innerHTML = t('btnStandard'); btn.classList.add('active'); }
            if(stdControls) stdControls.style.display = 'none';
            if(hmControls) hmControls.style.display = 'flex';
            if(legendPanel) legendPanel.style.display = 'block';
        } else {
            if(btn) { btn.innerHTML = t('btnHeatmap'); btn.classList.remove('active'); }
            if(stdControls) stdControls.style.display = 'flex';
            if(hmControls) hmControls.style.display = 'none';
            if(legendPanel) legendPanel.style.display = 'none';
            
            hideLowRunners = false;
            const hlCheckbox = document.getElementById('config-hide-low');
            if(hlCheckbox) hlCheckbox.checked = false;
        }

        const statusEl = document.getElementById('status');
        if(statusEl) { statusEl.innerText = t('statusCalc'); statusEl.style.color = '#8b949e'; }
        
        loadWarehouse(); 
    } catch (err) {
        console.error("UI Toggle Error:", err);
    }
});

// --- 7. RAYCASTER ---
const raycaster = new THREE.Raycaster();
const mouse = new THREE.Vector2();

window.addEventListener('click', (event) => {
    if (['SELECT','INPUT','BUTTON','LABEL'].includes(event.target.tagName)) return;

    mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
    mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
    raycaster.setFromCamera(mouse, camera);

    const intersects = raycaster.intersectObjects(scene.children);
    const clickedBox = intersects.find(hit => hit.object.userData && hit.object.userData.id);

    if (clickedBox) fetchSlotDetails(clickedBox.object.userData.id, clickedBox.object.userData.coord);
});

async function fetchSlotDetails(id, coord) {
    try {
        const panel = document.getElementById('slot-info-panel');
        if(!panel) return;
        
        panel.style.display = 'block';
        document.getElementById('si-title').innerText = `Slot ${coord}`;
        document.getElementById('si-status').innerText = `...`;
        document.getElementById('si-inventory').innerHTML = `<div style="color: #888;">Fetching...</div>`;

        const response = await fetch(`/api/slot-info?id=${id}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.error);

        const statusEl = document.getElementById('si-status');
        statusEl.innerText = data.slot.status;
        if(data.slot.status === 'OCCUPIED') statusEl.style.color = '#4ade80';
        else if(data.slot.status === 'BLOCKED') statusEl.style.color = '#ef4444';
        else if(data.slot.status === 'RESERVED') statusEl.style.color = '#f59e0b';
        else if(data.slot.status === 'INBOUND') statusEl.style.color = '#3b82f6';

        let invHtml = '';
        if (data.inventory && data.inventory.length > 0) {
            data.inventory.forEach(item => {
                invHtml += `
                <div style="background: rgba(0,0,0,0.3); padding: 8px; margin-bottom: 5px; border-left: 3px solid #60a5fa; border-radius: 4px;">
                    <div style="font-weight: bold; color: #fff;">${item.part_number}</div>
                    <div style="display: flex; justify-content: space-between; font-size: 0.8rem; color: #8b949e;">
                        <span>Qty: <strong style="color:#fff">${item.quantity}</strong></span>
                        <span style="font-family: monospace;">${item.lpn}</span>
                    </div>
                </div>`;
            });
        } else invHtml = `<div style="color: #8b949e; font-style: italic;">No physical items.</div>`;
        document.getElementById('si-inventory').innerHTML = invHtml;

        // 22.3.: Wire up the History Button
        const historyBtn = document.getElementById('btn-slot-history');
        if (historyBtn) {
            // Opens the new page in a new tab, passing the ID and Coordinate in the URL
            //historyBtn.onclick = () => window.open(`/slot-history.html?id=${id}&coord=${coord}`, '_blank');

            // CHANGED 15.5. 2026 ONLY PASSING ID->
            historyBtn.onclick = () => window.open(`/slot-history.html?id=${id}`, '_blank');
        }

    } catch (error) {
        document.getElementById('si-inventory').innerHTML = `<div style="color: #ef4444;">Error loading data.</div>`;
    }
}

// --- 8. START ANIMATION LOOP ---
function animate() { 
    requestAnimationFrame(animate); 
    
    // Smooth Camera Lerping Logic
    if (isCameraMoving) {
        camera.position.lerp(targetCameraPos, 0.05); // Glide position
        controls.target.lerp(targetCameraLookAt, 0.05); // Glide focal point
        
        // Stop calculating once we arrive
        if (camera.position.distanceTo(targetCameraPos) < 0.1) {
            isCameraMoving = false;
        }
    }
    
    controls.update(); 
    renderer.render(scene, camera); 
}

loadWarehouse();
animate();